<script setup lang="ts">
import type { EventVaultType } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useUrlSearchParams } from "@vueuse/core"
import { useForm } from "vee-validate"
import { computed, nextTick, ref, watchEffect } from "vue"
import { z } from "zod"
import { useCreateEventVault, useGetEventVault, useUpdateEventVault } from "@/api"
import { FreemiumBadge } from "@/components"
import {
  Button,
  Datepicker,
  FileInput,
  Form,
  FormSection,
  GooglePlaceInput,
  Icon,
  Input,
  LOCALE_CODES,
  LocaleCombobox,
  Select,
  Switch,
  Textarea,
} from "@/components/ui"
import { useEventDatetimeForm, useTranslation } from "@/composables"
import { useEventTypeSelect } from "@/composables/useEventTypeSelect"
import { ForbiddenException, ValidationException } from "@/http"
import { EventVault, EventVaultStatus, Location } from "@/models"
import { getCookie, locationValidation } from "@/utils"
import { useToast } from "../ui/ToastReka/useToast"

const props = defineProps<{
  eventVaultId?: number
  showVideoField?: boolean
  packageSubscriptionId?: string
  eventVaultType?: EventVaultType
}>()

const emit = defineEmits<{
  (e: "success", data: EventVault): void
  (e: "error", err?: unknown): void
}>()

const { t } = useTranslation()
const toast = useToast()
const urlParams = useUrlSearchParams<{ token?: string }>()

const queryClient = useQueryClient()

const validationErrors = ref<Record<string, string[]>>()
const isFormInitialised = ref<boolean>(false)
const enableEventTime = ref<boolean>(false)

const { data: eventVault, isLoading: isLoadingEventVault } = useGetEventVault(
  props.eventVaultId,
  { include: ["welcomevid"] },
)

const { isLoadingEventTypes, selectEventTypes, isValidEventType, applyDefault } = useEventTypeSelect({
  storedTypeId: () => eventVault.value?.eventType,
})

const validationSchema = toTypedSchema(
  z.object({
    type: z.number().refine(type => isValidEventType(type)),
    title: z.string().min(1, t("event_details_form.validation.title")).max(40, t("event_details_form.validation.title_max")),
    eventHeader: z.string().max(28, t("event_details_form.validation.header_max")).optional(),
    description: z.string().min(1, t("event_details_form.validation.description_required")),
    eventDate: z.date(),
    eventEndDate: z.date().optional(),
    timezone: z.string().optional(),
    coverImage: z
      .optional(z.any().refine(file => file instanceof File, t("event_details_form.validation.cover_image")))
      .nullable(),
    welcomeVideo: z.optional(
      z
        .any()
        .refine(file => file instanceof File, t("event_details_form.validation.welcome_video"))
        .nullable(),
    ),
    location: locationValidation(),
    communicationLocale: z.string().optional().refine(
      val => !val || LOCALE_CODES.includes(val),
      t("event_details_form.validation.communication_locale_invalid"),
    ),
  })
    // when time selection is enabled, a valid google place location must be selected
    .refine((fields) => {
      if (!enableEventTime.value)
        return true
      return Boolean(fields.location?.googleId)
    }, {
      message: t("event_details_form.validation.location_required_timezone"),
      path: ["location"],
    })
    .refine((fields) => {
      // eslint-disable-next-line ts/no-use-before-define
      if (!fields.location?.googleId || isLoadingLocationTimezone.value)
        return true
      return Boolean(fields.timezone)
    }, {
      message: t("event_details_form.validation.timezone_required"),
      path: ["location"],
    })
    .refine((fields) => {
      if (!fields.eventEndDate)
        return true

      return fields.eventEndDate > fields.eventDate
    }, {
      message: t("event_details_form.validation.end_date_after_start"),
      path: ["eventEndDate"],
    }),
)

const { handleSubmit, setFieldValue, errors, values } = useForm({
  validationSchema,
  initialValues: {
    type: undefined,
    title: "",
    eventHeader: "",
    description: "",
    eventDate: undefined,
    eventEndDate: undefined,
    timezone: undefined,
    location: undefined,
    communicationLocale: undefined,
  },
})

const {
  eventEndDateForSubmission,
  eventDateForSubmission,
  initializeEventEndDate,
  initializeEventDate,
  isLoadingLocationTimezone,
} = useEventDatetimeForm({ values, setFieldValue }, enableEventTime)

async function initialiseFormValues(ev: EventVault) {
  if (isFormInitialised.value)
    return

  if (ev.eventType) {
    setFieldValue("type", ev.eventType)
  }
  if (ev.title) {
    setFieldValue("title", ev.title)
  }
  if (ev.eventHeader) {
    setFieldValue("eventHeader", ev.eventHeader)
  }
  if (ev.description) {
    setFieldValue("description", ev.description)
  }
  if (ev.eventDate) {
    initializeEventDate(ev.eventDate, ev.timezone)
  }
  if (ev.eventEndDate) {
    initializeEventEndDate(ev.eventEndDate, ev.timezone)
  }
  if (ev.location) {
    setFieldValue("location", { ...ev.location })
  }
  setFieldValue("communicationLocale", ev.communicationLocale ?? (LOCALE_CODES.includes(navigator.language) ? navigator.language : "en-US"))

  isFormInitialised.value = true
}

const { mutate: createEventMutate, isPending: isPendingCreate } = useCreateEventVault()
const { mutate: updateEventMutation, isPending: isPendingUpdate } = useUpdateEventVault()

applyDefault({ values, setFieldValue }, {
  forcedType: () => props.eventVaultType,
  hasExistingRecord: () => Boolean(props.eventVaultId),
})

const onSubmit = handleSubmit((values) => {
  if (isLoadingLocationTimezone.value) {
    return
  }

  nextTick(() => window.scrollTo({ top: 0, behavior: "smooth" }))

  const eventUpdates = new EventVault({
    ...eventVault.value,
    id: props.eventVaultId,
    title: values.title,
    eventHeader: values.eventHeader,
    description: values.description,
    eventDate: eventDateForSubmission.value,
    eventEndDate: eventEndDateForSubmission.value,
    coverImage: values.coverImage,
    coverImageUrl: values.coverImage === null ? undefined : eventVault.value?.coverImageUrl,
    welcomeVideo: values.welcomeVideo,
    welcomeVideoUrl: values.welcomeVideo === null ? undefined : eventVault.value?.welcomeVideoUrl,
    eventType: values.type,
    timezone: enableEventTime.value ? values.timezone : undefined,
    location: values?.location ? new Location(values.location) : undefined,
    communicationLocale: values.communicationLocale,
  })

  if (!eventUpdates.id) {
    const affiliateCookie = getCookie("slicewp_aff")

    createEventMutate(
      { eventVault: eventUpdates, paymentToken: urlParams.token ?? "", packageSubscriptionId: props.packageSubscriptionId, ...(affiliateCookie !== null && { referrerId: affiliateCookie }) },
      {
        onSuccess(data) {
          emit("success", data)
        },
        onError(error) {
          if (error instanceof ForbiddenException) {
            toast.error({
              title: t("onboarding.configure.free_event_limit_title"),
              description: t("onboarding.configure.free_event_limit_description"),
              icon: "Crown",
            })

            return
          }

          if (error instanceof ValidationException) {
            validationErrors.value = error.validationErrors
          }
          emit("error", error)
        },
      },
    )
  }
  else {
    updateEventMutation(eventUpdates, {
      onSuccess: async (data) => {
        emit("success", data)
        await queryClient.invalidateQueries({ queryKey: ["getEventVault"] })
      },
      onError: (err: unknown) => {
        if (err instanceof ValidationException) {
          validationErrors.value = err.validationErrors
        }
        emit("error", err)
      },
    })
  }
})

const isLoading = computed(() => {
  return Boolean(
    isLoadingEventVault.value || isLoadingEventTypes.value || isPendingUpdate.value || isPendingCreate.value,
  )
})

const isFreemiumEvent = computed(() => eventVault.value?.freemiumMode)

function isEventDateDisabled(eventVault?: EventVault): boolean {
  if (!eventVault) {
    return false
  }

  const disabledStatus = [EventVaultStatus.Closed, EventVaultStatus.Expired, EventVaultStatus.Archived]

  return disabledStatus.includes(eventVault.status)
}

watchEffect(() => {
  if (eventVault.value) {
    initialiseFormValues(eventVault.value)
  }
})

const datetimeLabel = computed(() => {
  const albumsEnabled = eventVault.value?.eventVaultSettings?.albumsEnabled
  if (enableEventTime.value) {
    return albumsEnabled
      ? t("event_details_form.form.package_start_date_time")
      : t("event_details_form.form.start_date_and_time")
  }
  return albumsEnabled
    ? t("event_details_form.form.package_start")
    : t("event_details_form.form.start_date")
})
</script>

<template>
  <div v-show="isLoading" class="flex flex-col gap-8">
    <div class="gpx-skeleton w-full h-14" />
    <div class="gpx-skeleton w-full h-14" />
    <div class="gpx-skeleton w-full h-14" />
    <div class="gpx-skeleton w-full h-32" />
    <div class="gpx-skeleton w-full h-14" />
    <div class="gpx-skeleton w-48 h-12" />
  </div>
  <Form v-show="!isLoading" gap="lg" @submit="onSubmit">
    <FormSection
      :title="t('create_event_page.event_details_heading')"
      :description="t('onboarding.configure.get_started')"
      icon="Cards"
      variant="divider"
    >
      <Select v-show="!eventVaultType" name="type" :items="selectEventTypes" :label="t('event_details_form.form.type_label')" />
      <Input name="title" :label="t('event_details_form.form.name_label')" />
      <Input
        name="eventHeader"
        :label="t('event_details_form.form.header_label')"
        :hint="t('event_details_form.form.header_hint')"
      />
      <Textarea name="description" :label="t('event_details_form.form.description_label')" rows="12" />
    </FormSection>

    <FormSection
      :title="t('create_event_page.where_when_heading')"
      :description="t('onboarding.configure.date_hint')"
      icon="Location"
      variant="divider"
    >
      <GooglePlaceInput
        id="location"
        name="location.displayAddress"
        :label="t('event_details_form.form.location_label')"
        :error="errors.location"
        :disabled="isEventDateDisabled(eventVault)"
        :clearable="!isEventDateDisabled(eventVault)"
        @error="emit('error')"
        @update:location="(loc) => setFieldValue('location', loc)"
      />
      <div class="flex flex-col gap-3">
        <div class="max-w-md">
          <Switch
            v-model="enableEventTime"
            name="addTime"
            :label="t('event_details_form.form.enable_time_label')"
            :disabled="isEventDateDisabled(eventVault)"
          />
        </div>
        <Datepicker
          name="eventDate"
          :enable-time-picker="enableEventTime"
          :label="datetimeLabel"
          :disabled="isEventDateDisabled(eventVault)"
          :clearable="false"
        />
        <Datepicker
          v-if="enableEventTime"
          name="eventEndDate"
          :enable-time-picker="enableEventTime"
          :label="enableEventTime ? t('event_details_form.form.end_date_and_time') : t('event_details_form.form.end_date')"
          :disabled="isEventDateDisabled(eventVault)"
          :min-date="values.eventDate"
        />
      </div>
    </FormSection>

    <FormSection
      :title="t('create_event_page.cover_image_heading')"
      :description="t('event_details_form.form.image_hint')"
      icon="Images"
      variant="divider"
    >
      <div>
        <label for="coverImage" class="mb-2 block text-base font-bold font-heading">{{ t("event_details_form.form.image_label") }}</label>
        <FileInput
          name="coverImage"
          accept="image/*"
          :preview="eventVault?.coverImageUrl"
          preview-type="image"
          :accept-message="t('event_details_form.form.image_accept')"
        />
      </div>

      <div v-if="eventVault?.eventVaultSettings?.videoEnabled || showVideoField" class="border-t border-gray-100 pt-6">
        <div class="mb-5 flex items-center gap-2">
          <label for="welcomeVideo" class="block text-base font-bold font-heading">
            {{ t("event_details_form.form.video_label") }}
          </label>
          <FreemiumBadge v-if="isFreemiumEvent" variant="icon" />
        </div>
        <FileInput
          :disabled="isFreemiumEvent"
          name="welcomeVideo"
          :preview="eventVault?.welcomeVideoUrl"
          preview-type="video"
          accept="video/*"
          :accept-message="t('event_details_form.form.video_accept')"
        />
        <p v-if="values?.welcomeVideo" class="mt-2 flex items-start gap-2 text-sm text-gray-500">
          <Icon icon="CircleInfo" size="sm" class="shrink-0" />
          {{ t("event_details_form.form.video_hint") }}
        </p>
      </div>
    </FormSection>

    <FormSection
      v-if="eventVaultId"
      :title="t('event_details_form.communication_preferences.title')"
      :description="t('event_details_form.communication_preferences.description')"
      icon="Globe"
      variant="divider"
    >
      <LocaleCombobox
        name="communicationLocale"
        :label="t('event_details_form.form.communication_locale_label')"
        :error="validationErrors?.CommunicationLocale?.[0]"
      />
    </FormSection>

    <template #actions>
      <div class="flex justify-end">
        <Button type="submit" size="lg" class="w-full lg:w-40">
          {{ t("save") }}
        </Button>
      </div>
    </template>
  </Form>
</template>
